import type { ElementType, HTMLAttributes } from 'react'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '@/lib/utils'

const typographyClassVariants = cva('', {
  variants: {
    variant: {
      pageTitle: 'text-3xl font-semibold tracking-[-0.01em] text-balance',
      sectionTitle: 'text-lg leading-6 font-semibold tracking-[-0.01em] text-balance',
      cardTitle: 'text-base leading-6 font-semibold tracking-[-0.01em] text-balance',
      description: 'text-sm leading-[1.5] text-pretty',
      eyebrow: 'text-xs leading-[1.2] font-semibold uppercase tracking-[0.12em]',
      h1: 'scroll-m-20 text-[38px] leading-[1.1] font-semibold tracking-[-0.01em]',
      h2: 'scroll-m-20 text-[31px] leading-[1.25] font-semibold tracking-[-0.01em] first:mt-0',
      h3: 'scroll-m-20 text-[25px] leading-[1.25] font-semibold tracking-[-0.01em]',
      h4: 'scroll-m-20 text-[21px] leading-[1.25] font-semibold tracking-[-0.01em]',
      h5: 'scroll-m-20 text-lg leading-[1.25] font-semibold tracking-[-0.01em]',
      bodyLarge: 'text-lg leading-[1.65] font-normal',
      body: 'text-base leading-[1.5] font-normal',
      bodySmall: 'text-sm leading-[1.5] font-normal',
      label: 'text-sm leading-[1.35] font-medium',
      labelStrong: 'text-sm leading-[1.35] font-semibold',
      supporting: 'text-sm leading-[1.5] font-normal',
      caption: 'text-xs leading-[1.4] font-normal',
      emphasis: 'text-lg font-semibold',
      blockquote: 'mt-6 border-l-2 pl-6 italic',
      inlineCode: 'relative rounded bg-muted px-[0.3rem] py-[0.2rem] text-sm font-semibold',
    },
    tone: {
      default: 'text-foreground',
      muted: 'text-muted-foreground',
      interaction: 'text-interaction',
      destructive: 'text-destructive',
      success: 'text-status-success-foreground',
      warning: 'text-status-warning-foreground',
      info: 'text-status-info-foreground',
      accent: 'text-accentSecondary-foreground',
      inherit: 'text-inherit',
    },
    weight: {
      inherit: '',
      normal: 'font-normal',
      medium: 'font-medium',
      semibold: 'font-semibold',
      bold: 'font-bold',
    },
    italic: {
      true: 'italic',
      false: '',
    },
    align: {
      inherit: '',
      left: 'text-left',
      center: 'text-center',
      right: 'text-right',
    },
    wrap: {
      inherit: '',
      pretty: 'text-pretty',
      balance: 'text-balance',
    },
    decoration: {
      none: '',
      underline: 'underline',
      lineThrough: 'line-through',
    },
    interaction: {
      none: '',
      group: 'transition-colors group-hover:text-interaction',
      hoverUnderline:
        'cursor-pointer underline-offset-4 hover:underline group-hover/index-table-cell:underline',
      link: 'underline decoration-1 underline-offset-4 hover:text-action-pressed hover:decoration-2 dark:hover:text-foreground',
    },
  },
  defaultVariants: {
    variant: 'body',
    weight: 'inherit',
    italic: false,
    align: 'inherit',
    wrap: 'inherit',
    decoration: 'none',
    interaction: 'none',
  },
})

type TypographyClassVariantProps = VariantProps<typeof typographyClassVariants>
export type TypographyVariant = NonNullable<TypographyClassVariantProps['variant']>
export type TypographyTone = NonNullable<TypographyClassVariantProps['tone']>

const defaultTypographyTones: Partial<Record<TypographyVariant, TypographyTone>> = {
  caption: 'muted',
  description: 'muted',
  eyebrow: 'muted',
  supporting: 'muted',
}

function typographyVariants({ variant, tone, ...options }: TypographyClassVariantProps = {}) {
  const resolvedVariant = variant ?? 'body'
  const resolvedTone = tone ?? defaultTypographyTones[resolvedVariant] ?? 'default'
  return typographyClassVariants({ variant: resolvedVariant, tone: resolvedTone, ...options })
}

const defaultTypographyElements: Record<TypographyVariant, ElementType> = {
  pageTitle: 'h1',
  sectionTitle: 'h3',
  cardTitle: 'h4',
  description: 'p',
  eyebrow: 'p',
  h1: 'h1',
  h2: 'h2',
  h3: 'h3',
  h4: 'h4',
  h5: 'h5',
  bodyLarge: 'p',
  body: 'p',
  bodySmall: 'p',
  label: 'span',
  labelStrong: 'span',
  supporting: 'span',
  caption: 'span',
  emphasis: 'span',
  blockquote: 'blockquote',
  inlineCode: 'code',
}

export type TypographyProps = HTMLAttributes<HTMLElement> &
  Omit<TypographyClassVariantProps, 'italic'> & {
    as?: ElementType
    italic?: boolean
  }

function Typography({
  align = 'inherit',
  as,
  children,
  className,
  decoration = 'none',
  italic = false,
  interaction = 'none',
  tone,
  variant = 'body',
  weight = 'inherit',
  wrap = 'inherit',
  ...props
}: TypographyProps) {
  const resolvedVariant = variant ?? 'body'
  const Component = as ?? defaultTypographyElements[resolvedVariant]
  const resolvedTone = tone ?? defaultTypographyTones[resolvedVariant] ?? 'default'

  return (
    <Component
      data-slot="text"
      data-variant={resolvedVariant}
      data-tone={resolvedTone}
      data-weight={weight}
      data-align={align}
      data-decoration={decoration}
      data-interaction={interaction}
      data-wrap={wrap}
      className={cn(
        typographyVariants({
          align,
          decoration,
          italic,
          interaction,
          tone: resolvedTone,
          variant: resolvedVariant,
          weight,
          wrap,
        }),
        className
      )}
      {...props}
    >
      {children}
    </Component>
  )
}

export { Typography, typographyVariants }
